import { UsdZipWriter } from './UsdZip';
import { exportToUsd, type UsdData } from './UsdUtils';

import { GeometryExportFormat } from '@egr/xbox/utils/FileFormats';
import { getFilename } from '@egr/xbox/utils/String';
import { getOWDSShareFileUrl } from '@egr/xbox/services/OWDS';

import { BoundingBox } from '@egr/wcf/modules/core/mdl';
import type { ModelManager, SceneElement } from '@egr/wcf/modules/core/mdl';
import { RoomElement } from '@egr/room/modules/mdl/RoomElement';
import { Wall } from '@egr/room/modules/mdl/Wall';
import { WallOpening } from '@egr/room/modules/mdl/WallOpening';
import { WallAddition } from '@egr/room/modules/mdl/WallAddition';

export function getFilteredElements(pElements: Array<SceneElement>, includeWalls: boolean): ReadonlyArray<SceneElement> {
    return pElements.filter((pElement) => {
        if (
            pElement instanceof RoomElement &&
            ((pElement.type === 'Sphere') || (pElement.type === 'WallSideDummy'))
        ) {
            return (false);
        }

        if (
            !includeWalls &&
            (pElement instanceof Wall ||
                (pElement instanceof WallOpening) ||
                (pElement instanceof WallAddition) ||
                (pElement instanceof RoomElement && pElement.type === 'Floor'))
        ) {
            return (false);
        }

        return (true);
    });
}

export async function usdzExport(
    model: ModelManager,
    sceneElements: ReadonlyArray<SceneElement> | null = null,
    scaleFactor?: number
): Promise<Blob> {
    const usdData: UsdData = await exportToUsd(model.materials, sceneElements ?? model.elements,
        scaleFactor ?? 1.0);
    const zipWriter = new UsdZipWriter();
    zipWriter.addFile('a.usda', usdData.usda);

    usdData.textureFiles.forEach((pData, pName) =>
    {
        zipWriter.addFile(pName, pData);
    });

    return (zipWriter.generateZip());
}

export interface CreateUSDZOptions {
    includeWalls?: boolean;
    scale?: number;
    onBefore?: () => Promise<void>;
    onAfter?: () => Promise<void>;
}

export async function getLocalUSDZBlob(    modelManager: ModelManager,
    config: CreateUSDZOptions = {},
    elements?: Array<SceneElement>
): Promise<Blob> {
    await config?.onBefore?.();

    const elementsForExport: ReadonlyArray<SceneElement> = elements ?? getFilteredElements(modelManager.elements.slice(), config.includeWalls ?? false);

    let scaleFactor: number = 1;

    if (config.scale != null && config.scale !== 1) {
        const boundingBox: BoundingBox = BoundingBox.FromElements(elementsForExport);
        const originalHeight: number = boundingBox.max.y - boundingBox.min.y;
        scaleFactor = config.scale / originalHeight;
    }

    const result = await usdzExport(modelManager, elementsForExport, scaleFactor);

    await config?.onAfter?.();

    return result;
}

export async function getLocalUSDZ(
    modelManager: ModelManager,
    config: CreateUSDZOptions = {},
    elements?: Array<SceneElement>
): Promise<string> {
    const content: Blob = await getLocalUSDZBlob(modelManager, config, elements);

    const result = await getOWDSShareFileUrl({
        filename: getFilename('export', GeometryExportFormat.USDZ),
        content,
    });

    return result;
}
